import {
  Box,
  Button,
  capitalize,
  CardContent,
  Divider,
  FormControl,
  FormLabel,
  Radio,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type { Account } from '@repo/api-gw-sdk';
import { CloudAccountType, Provider } from '@repo/api-gw-sdk';
import React, { useState } from 'react';

import { useDAL } from '@/contexts/useDAL';
import { useEnvironment } from '@/contexts/useEnvironment';
import { useUser } from '@/contexts/useUser';

import { Loader } from '../layout/loading';
import { PanelWrapper } from '../panels/panelWrapper';
import { CopyableCode } from '../shared/copyableCode';

enum GcpDeploymentMethod {
  Bash = 'Bash',
  Terraform = 'Terraform',
}

interface UpdateAccountRoleGcpInnerPanelProps {
  accountType: CloudAccountType.Source | CloudAccountType.Restore;
  headerTitle: string;
  onUpdated: (success: boolean, sourceAccountId: string) => void;
  initialProjectId: string;
  onClose: () => void;
}

export function UpdateAccountRoleGcpInnerPanel(
  props: UpdateAccountRoleGcpInnerPanelProps
) {
  const user = useUser();
  const dal = useDAL();
  const [projectId, setProjectId] = useState(props.initialProjectId);
  const { gcpControlPlane } = useEnvironment();
  const [isCreated, setIsCreated] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [deploymentMethod, setDeploymentMethod] = useState(
    GcpDeploymentMethod.Terraform
  );

  const { data: body } = dal.cloudAccounts.scanning.useList();
  const scanningProjectId = body?.accounts?.find(
    (account: Account) => account.cloudProvider === Provider.Gcp
  )?.providerAccountId;

  const key = props.accountType.toLowerCase() as 'source' | 'restore';

  const { data: existingAccounts } = dal.cloudAccounts[key].useList();

  const doneOnClick = async () => {
    setHasError(false);
    setIsCreating(true);

    let result = { id: '', success: false };

    const existingAccount = existingAccounts?.accounts.find(
      (account) => account.providerAccountId === props.initialProjectId
    );

    if (existingAccount) {
      const syncFunc =
        props.accountType === CloudAccountType.Source
          ? dal.cloudAccounts.source.syncPermissions
          : props.accountType === CloudAccountType.Restore
            ? dal.cloudAccounts.restore.syncPermissions
            : undefined;
      if (syncFunc) {
        result = await syncFunc(existingAccount.id)
          .then(() => ({ success: true, id: existingAccount.id }))
          .catch(() => ({ success: false, id: '' }));
      }
    }
    setIsCreating(false);
    if (result.success) {
      setIsCreated(true);
      setTimeout(() => props.onUpdated(true, result.id), 1000);
    } else {
      setHasError(true);
    }
  };

  const code = `curl -fsSL https://eon-public-b2b628cc-1d96-4fda-8dae-c3b1ad3ea03b.s3.amazonaws.com/gcp-eon-setup.sh -o gcp-${props.accountType.toLowerCase()}-setup.sh && chmod +x gcp-${props.accountType.toLowerCase()}-setup.sh && ./gcp-${props.accountType.toLowerCase()}-setup.sh --account-type ${capitalize(props.accountType.toLowerCase())} --project-id ${projectId || '<project-id>'} --eon-account-id ${user.eonAccount?.id} --scanning-project-id ${scanningProjectId} --control-plane-service-account ${gcpControlPlane}`;

  return (
    <PanelWrapper
      rounded
      header={{ title: props.headerTitle, onClose: props.onClose }}
    >
      <CardContent>
        <Box className='py-[40px]'>
          <>
            <Box className='my-[15px]'>
              Before continuing, make sure you have permissions to create,
              update and manage a service account and assign roles in your GCP
              project.
            </Box>
            <Box className='my-[15px] mb-[30px]'>
              <Stack direction='column' gap='16px'>
                <Typography marginBottom='8px' variant='body2'>
                  Deployment method
                </Typography>
                <Stack
                  className='cursor-pointer'
                  direction='row'
                  alignItems='center'
                  gap='12px'
                  onClick={() => setDeploymentMethod(GcpDeploymentMethod.Bash)}
                >
                  <Radio
                    data-testid='specific-vol-radio'
                    size='small'
                    checked={deploymentMethod === GcpDeploymentMethod.Bash}
                  />
                  <Typography variant='body2'>Bash (gcloud CLI)</Typography>
                </Stack>
                <Stack
                  className='cursor-pointer'
                  direction='row'
                  alignItems='center'
                  gap='12px'
                  onClick={() =>
                    setDeploymentMethod(GcpDeploymentMethod.Terraform)
                  }
                >
                  <Radio
                    data-testid='specific-vol-radio'
                    size='small'
                    checked={deploymentMethod === GcpDeploymentMethod.Terraform}
                  />
                  <Typography variant='body2'>Terraform</Typography>
                </Stack>
              </Stack>
            </Box>
            <Divider className='mb-[24px]' />
            {deploymentMethod === GcpDeploymentMethod.Bash && (
              <>
                <FormControl size='small' className='mb-[12px] w-full'>
                  <FormLabel>Project ID</FormLabel>
                  <TextField
                    slotProps={{
                      htmlInput: { maxLength: 30, 'data-testid': 'project-id' },
                    }}
                    disabled={true}
                    value={projectId}
                    placeholder='Example: my-google-cloud-project'
                    onChange={(event) => setProjectId(event.target.value)}
                  />
                </FormControl>
                {projectId && (
                  <>
                    <Box className='my-[15px]'>
                      Run this command to deploy Eon&apos;s roles to your GCP
                      project:
                    </Box>
                    <Box
                      className='flex flex-col items-center justify-center my-[30px]'
                      gap='8px'
                    >
                      <CopyableCode
                        code={code}
                        codeClassName='whitespace-pre-wrap'
                      />
                    </Box>{' '}
                  </>
                )}
              </>
            )}
            {deploymentMethod === GcpDeploymentMethod.Terraform && (
              <>
                <FormControl size='small' className='mb-[12px] w-full'>
                  <FormLabel>Project ID</FormLabel>
                  <TextField
                    slotProps={{
                      htmlInput: { maxLength: 30, 'data-testid': 'project-id' },
                    }}
                    value={projectId}
                    disabled={true}
                    placeholder='Example: my-google-cloud-project'
                    onChange={(event) => setProjectId(event.target.value)}
                  />
                </FormControl>
                <ol className='list-decimal mx-[20px]'>
                  <li className='mb-[8px]'>
                    <Typography
                      variant='body2'
                      className='mb-[20px]'
                      color='text.secondary'
                    >
                      <b>
                        Add the Eon Terraform module to your deployment file
                      </b>
                      <br />
                      Add this snippet to your Terraform deployment file and
                      apply.
                    </Typography>
                  </li>
                </ol>
                {projectId && (
                  <Box
                    className='flex flex-col items-center justify-center my-[30px]'
                    gap='8px'
                  >
                    <CopyableCode
                      code={`\
module "gcp_${props.accountType.toLowerCase()}" {
    source = "https://eon-public-b2b628cc-1d96-4fda-8dae-c3b1ad3ea03b.s3.amazonaws.com/gcp-eon-setup.zip"
    account_type = "${props.accountType.toLowerCase()}"
    project_id = "${projectId || '<project-id>'}"
    eon_account_id = "${user.eonAccount?.id}"
    scanning_project_id = "${scanningProjectId}"
    control_plane_service_account = "${gcpControlPlane}"
}`}
                    />
                  </Box>
                )}
                <ol start={2} className='list-decimal mx-[20px]'>
                  <li>
                    <Typography
                      variant='body2'
                      className='mb-[20px]'
                      color='text.secondary'
                    >
                      <b>Confirm the deployment</b>
                      <br />
                      After the Terraform deployment is done, click{' '}
                      <b>I&apos;m Done</b> so Eon can confirm the permissions
                      and update the account.
                    </Typography>
                  </li>
                </ol>
              </>
            )}
            {!hasError && !isCreating && !isCreated && (
              <Box className='flex items-center gap-[8px] justify-between'>
                <Typography variant='body2'>
                  {projectId &&
                    deploymentMethod === GcpDeploymentMethod.Bash && (
                      <>
                        After the roles are installed, click{' '}
                        <b>I&apos;m Done</b> so Eon can confirm the permissions
                        and update the account.
                      </>
                    )}
                </Typography>
                <Button
                  variant='contained'
                  data-testid='aws-console-done'
                  disabled={!projectId}
                  onClick={() => void doneOnClick()}
                >
                  I&apos;m Done
                </Button>
              </Box>
            )}
            {isCreated && !isCreating && (
              <Box
                className='flex items-center gap-[4px] ml-[20px] h-[48px]'
                sx={{ color: 'var(--mui-palette-success-main)' }}
              >
                <i className='material-symbols-check w-[20px] h-[20px]' />{' '}
                Account updated
              </Box>
            )}
            {isCreating && (
              <Box className='flex items-center gap-[8px] ml-[20px] justify-between'>
                <Stack alignItems='center' direction='row' gap='12px'>
                  <Loader variant='inline' />
                  {isCreating && (
                    <Typography variant='body2'>
                      Trying to connect...
                    </Typography>
                  )}
                </Stack>
              </Box>
            )}
            {hasError && !isCreating && (
              <Box
                className='flex items-center gap-[4px] ml-[20px] h-[48px]'
                sx={{ color: 'var(--mui-palette-error-main)' }}
              >
                <i className='material-symbols-close-rounded w-[20px] h-[20px]' />{' '}
                Something went wrong
                <Divider
                  orientation='vertical'
                  flexItem
                  className='mx-[6px] my-auto h-[20px]'
                />
                <Button variant='contained' onClick={() => void doneOnClick()}>
                  Try Again
                </Button>
              </Box>
            )}
          </>
        </Box>
      </CardContent>
    </PanelWrapper>
  );
}
